// Globalny silnik Pomodoro: jeden tick + alarm na poziomie aplikacji, dzięki
// czemu faza odlicza i dzwoni niezależnie od aktualnej podstrony.
// Stan i historia trzymane w localStorage — moduł nie korzysta z API.
// Stan współdzielony przez useState; interwał jako singleton modułu,
// a odtwarzanie dźwięku w kanale 'pomodoro' z useAlarmSound.

import { ALARM_SOUNDS, alarmSoundSrc, useAlarmSound } from '~/composables/useAlarmSound'
import { toApiDate } from '~/utils/dates'
import { t } from '@/i18n/index.js'

export type PomodoroPhase = 'work' | 'short_break' | 'long_break'
export type PomodoroMode = 'idle' | 'running' | 'paused' | 'finished'

export interface PomodoroState {
    taskName: string
    workMinutes: number
    shortBreakMinutes: number
    longBreakMinutes: number
    longBreakEvery: number      // po ilu pomodoro wypada długa przerwa
    autoStart: boolean
    workSoundKey: string
    breakSoundKey: string
    phase: PomodoroPhase
    mode: PomodoroMode
    configuredSeconds: number   // długość trwającej fazy — zmiana ustawień nie psuje pierścienia
    endTime: number
    remainingMs: number
    completedInCycle: number    // ukończone pomodoro od ostatniej długiej przerwy
}

export interface PomodoroDay {
    count: number
    focusSeconds: number
}

// Te same dźwięki co w minutnikach — bez duplikowania listy.
export const POMODORO_SOUNDS = ALARM_SOUNDS

// Gotowe zestawy czasów (praca / krótka przerwa / długa przerwa / co ile długa).
export const POMODORO_PRESETS = [
    { key: 'classic', get label() { return t('tools.pomodoro.presets.classic') }, work: 25, short: 5, long: 15, every: 4 },
    { key: 'short', get label() { return t('tools.pomodoro.presets.short') }, work: 15, short: 3, long: 10, every: 4 },
    { key: 'deep', get label() { return t('tools.pomodoro.presets.deep') }, work: 50, short: 10, long: 30, every: 2 },
    { key: 'sprint', get label() { return t('tools.pomodoro.presets.sprint') }, work: 90, short: 15, long: 30, every: 2 },
]

export const PHASE_LABELS: Record<PomodoroPhase, string> = {
    get work() { return t('tools.pomodoro.phases.work') },
    get short_break() { return t('tools.pomodoro.phases.shortBreak') },
    get long_break() { return t('tools.pomodoro.phases.longBreak') },
}

const STATE_KEY = 'rountine.pomodoro.state'
const HISTORY_KEY = 'rountine.pomodoro.history'
const HISTORY_DAYS = 30

const newState = (): PomodoroState => ({
    taskName: '',
    workMinutes: 25,
    shortBreakMinutes: 5,
    longBreakMinutes: 15,
    longBreakEvery: 4,
    autoStart: false,
    workSoundKey: 'chimes',
    breakSoundKey: 'marimba',
    phase: 'work',
    mode: 'idle',
    configuredSeconds: 25 * 60,
    endTime: 0,
    remainingMs: 0,
    completedInCycle: 0,
})

// --- singletony modułu (żyją przez cały czas życia SPA) ---
let intervalId: any = null
let fullscreenListenerAttached = false
let engineStarted = false

export const usePomodoro = () => {
    const state = useState<PomodoroState>('pomodoro.state', newState)
    const history = useState<Record<string, PomodoroDay>>('pomodoro.history', () => ({}))
    const now = useState<number>('pomodoro.now', () => Date.now())
    const fullscreen = useState<boolean>('pomodoro.fullscreen', () => false)

    // Sygnał gra tylko dopóki faza jest domknięta i czeka na reakcję.
    const alarm = useAlarmSound('pomodoro', () => state.value.mode === 'finished')

    const soundSrc = alarmSoundSrc

    const clampInt = (value: any, min: number, max: number, fallback: number): number => {
        const n = Math.floor(Number(value))
        if (!Number.isFinite(n)) return fallback
        return Math.min(max, Math.max(min, n))
    }

    // --- czasy faz ---
    const phaseMinutes = (phase: PomodoroPhase): number => {
        const s = state.value
        if (phase === 'work') return s.workMinutes
        if (phase === 'short_break') return s.shortBreakMinutes
        return s.longBreakMinutes
    }
    const phaseSeconds = (phase: PomodoroPhase): number => Math.max(1, phaseMinutes(phase)) * 60

    // --- persystencja stanu ---
    const save = () => {
        if (!import.meta.client) return
        try {
            localStorage.setItem(STATE_KEY, JSON.stringify(state.value))
        } catch { /* brak miejsca / tryb prywatny */ }
    }

    const load = () => {
        if (!import.meta.client) return
        try {
            const raw = localStorage.getItem(STATE_KEY)
            if (!raw) return
            const d = JSON.parse(raw)
            const s = state.value

            if (typeof d.taskName === 'string') s.taskName = d.taskName
            s.workMinutes = clampInt(d.workMinutes, 1, 240, s.workMinutes)
            s.shortBreakMinutes = clampInt(d.shortBreakMinutes, 1, 120, s.shortBreakMinutes)
            s.longBreakMinutes = clampInt(d.longBreakMinutes, 1, 120, s.longBreakMinutes)
            s.longBreakEvery = clampInt(d.longBreakEvery, 2, 12, s.longBreakEvery)
            if (typeof d.autoStart === 'boolean') s.autoStart = d.autoStart
            if (POMODORO_SOUNDS.some(x => x.key === d.workSoundKey)) s.workSoundKey = d.workSoundKey
            if (POMODORO_SOUNDS.some(x => x.key === d.breakSoundKey)) s.breakSoundKey = d.breakSoundKey
            if (d.phase === 'work' || d.phase === 'short_break' || d.phase === 'long_break') s.phase = d.phase
            s.completedInCycle = clampInt(d.completedInCycle, 0, s.longBreakEvery, 0)
            s.configuredSeconds = clampInt(d.configuredSeconds, 1, 240 * 60, phaseSeconds(s.phase))

            if (d.mode === 'running' && Number.isFinite(d.endTime)) {
                s.endTime = d.endTime
                if (Date.now() >= d.endTime) finish()   // czas minął, gdy app była zamknięta
                else s.mode = 'running'
            } else if (d.mode === 'paused' && Number.isFinite(d.remainingMs)) {
                s.remainingMs = d.remainingMs
                s.mode = 'paused'
            } else if (d.mode === 'finished' && Number.isFinite(d.endTime)) {
                // Faza dobiegła końca przy zamkniętej apce — pomodoro było już
                // zaliczone przy zapisie, tu tylko wracamy do stanu "dzwoni".
                s.endTime = d.endTime
                s.mode = 'finished'
                if (!s.autoStart) startAlarm(currentSoundSrc(), true)
            }
        } catch { /* uszkodzony wpis */ }
    }

    // --- persystencja historii ---
    const saveHistory = () => {
        if (!import.meta.client) return
        try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(history.value))
        } catch { /* brak miejsca / tryb prywatny */ }
    }

    const loadHistory = () => {
        if (!import.meta.client) return
        try {
            const raw = localStorage.getItem(HISTORY_KEY)
            if (!raw) return
            const d = JSON.parse(raw)
            if (!d || typeof d !== 'object') return

            const cutoff = toApiDate(new Date(Date.now() - HISTORY_DAYS * 86400000))
            const clean: Record<string, PomodoroDay> = {}
            for (const [day, entry] of Object.entries<any>(d)) {
                if (day < cutoff) continue                       // starsze niż 30 dni odpada
                const count = Math.max(0, Math.floor(Number(entry?.count) || 0))
                const focusSeconds = Math.max(0, Math.floor(Number(entry?.focusSeconds) || 0))
                if (count || focusSeconds) clean[day] = { count, focusSeconds }
            }
            history.value = clean
            saveHistory()
        } catch { /* uszkodzony wpis */ }
    }

    // Zalicza ukończone pomodoro do dzisiejszego dnia.
    const recordPomodoro = (focusSeconds: number) => {
        const day = toApiDate(new Date())
        const entry = history.value[day] ?? { count: 0, focusSeconds: 0 }
        history.value = {
            ...history.value,
            [day]: { count: entry.count + 1, focusSeconds: entry.focusSeconds + focusSeconds },
        }
        saveHistory()
    }

    // --- audio / alarm ---
    const currentSoundSrc = () =>
        soundSrc(state.value.phase === 'work' ? state.value.workSoundKey : state.value.breakSoundKey)

    // Z auto-startem sygnał gra raz (kolejna faza rusza natychmiast), bez
    // auto-startu dzwoni w pętli — i wtedy narasta, aż zareagujesz.
    const startAlarm = (src: string, loop: boolean) => alarm.play(src, loop)

    const stopAlarm = () => alarm.stop()

    const stopTestSound = () => alarm.stopTest()

    const testSound = (soundKey: string) => alarm.playTest(soundSrc(soundKey))

    // --- cykl faz ---
    // Po pracy: długa przerwa, gdy domknął się cykl; inaczej krótka. Po przerwie praca.
    const nextPhase = (): PomodoroPhase => {
        const s = state.value
        if (s.phase !== 'work') return 'work'
        return s.completedInCycle >= s.longBreakEvery ? 'long_break' : 'short_break'
    }

    // Ustawia fazę bez odliczania; wyjście z długiej przerwy zeruje cykl.
    const goToPhase = (phase: PomodoroPhase) => {
        const s = state.value
        if (s.phase === 'long_break' && phase === 'work') s.completedInCycle = 0
        s.phase = phase
        s.mode = 'idle'
        s.configuredSeconds = phaseSeconds(phase)
        s.endTime = 0
        s.remainingMs = 0
    }

    // --- przejścia stanu ---
    const start = () => {
        const s = state.value
        stopAlarm()
        s.configuredSeconds = phaseSeconds(s.phase)
        s.endTime = Date.now() + s.configuredSeconds * 1000
        s.mode = 'running'
        save()
    }

    const finish = () => {
        const s = state.value
        s.mode = 'finished'

        // Tylko praca liczy się do statystyk i do cyklu.
        if (s.phase === 'work') {
            recordPomodoro(s.configuredSeconds || phaseSeconds('work'))
            s.completedInCycle = Math.min(s.longBreakEvery, s.completedInCycle + 1)
        }

        if (s.autoStart) {
            startAlarm(currentSoundSrc(), false)
            goToPhase(nextPhase())
            start()
            return
        }

        save()
        startAlarm(currentSoundSrc(), true)
    }

    const pause = () => {
        const s = state.value
        if (s.mode !== 'running') return
        s.remainingMs = Math.max(0, s.endTime - Date.now())
        s.mode = 'paused'
        save()
    }

    const resume = () => {
        const s = state.value
        if (s.mode !== 'paused') return
        s.endTime = Date.now() + s.remainingMs
        s.mode = 'running'
        save()
    }

    // "Dalej" po zakończonej fazie — przechodzi do kolejnej i czeka na start.
    const next = () => {
        stopAlarm()
        goToPhase(nextPhase())
        save()
    }

    // Pomiń fazę — bez zaliczania pomodoro (praca nie trafia do statystyk).
    const skip = () => {
        stopAlarm()
        goToPhase(nextPhase())
        save()
    }

    // Reset całego cyklu; historii nie rusza.
    const reset = () => {
        const s = state.value
        stopAlarm()
        s.phase = 'work'
        s.mode = 'idle'
        s.configuredSeconds = phaseSeconds('work')
        s.endTime = 0
        s.remainingMs = 0
        s.completedInCycle = 0
        save()
    }

    // --- ustawienia ---
    const setTaskName = (name: string) => { state.value.taskName = name; save() }

    const setPhaseMinutes = (phase: PomodoroPhase, minutes: any) => {
        const s = state.value
        const max = phase === 'work' ? 240 : 120
        const value = clampInt(minutes, 1, max, phaseMinutes(phase))
        if (phase === 'work') s.workMinutes = value
        else if (phase === 'short_break') s.shortBreakMinutes = value
        else s.longBreakMinutes = value
        save()
    }

    const setLongBreakEvery = (value: any) => {
        const s = state.value
        s.longBreakEvery = clampInt(value, 2, 12, s.longBreakEvery)
        s.completedInCycle = Math.min(s.completedInCycle, s.longBreakEvery)
        save()
    }

    const applyPreset = (preset: typeof POMODORO_PRESETS[number]) => {
        const s = state.value
        s.workMinutes = preset.work
        s.shortBreakMinutes = preset.short
        s.longBreakMinutes = preset.long
        s.longBreakEvery = preset.every
        s.completedInCycle = Math.min(s.completedInCycle, s.longBreakEvery)
        save()
    }

    const toggleAutoStart = () => { state.value.autoStart = !state.value.autoStart; save() }

    // Przywraca fabryczne ustawienia i zeruje cykl; historia zostaje.
    const restoreDefaults = () => {
        stopAlarm()
        stopTestSound()
        Object.assign(state.value, newState())
        save()
    }

    // Czyści statystyki (licznik dnia i słupki); ustawień nie rusza.
    const clearHistory = () => {
        history.value = {}
        saveHistory()
    }

    const selectSound = (phase: 'work' | 'break', soundKey: string) => {
        if (!POMODORO_SOUNDS.some(s => s.key === soundKey)) return
        stopTestSound()
        if (phase === 'work') state.value.workSoundKey = soundKey
        else state.value.breakSoundKey = soundKey
        save()
    }

    // --- wartości pochodne ---
    const remainingSeconds = computed(() => {
        const s = state.value
        if (s.mode === 'idle') return phaseSeconds(s.phase)
        if (s.mode === 'finished') return 0
        if (s.mode === 'paused') return Math.max(0, Math.ceil(s.remainingMs / 1000))
        return Math.max(0, Math.ceil((s.endTime - now.value) / 1000))
    })

    const overtimeSeconds = computed(() =>
        state.value.mode === 'finished' ? Math.max(0, Math.floor((now.value - state.value.endTime) / 1000)) : 0)

    const progress = computed(() => {
        const s = state.value
        if (s.mode === 'finished') return 1
        if (s.mode === 'idle') return 0
        const total = s.configuredSeconds || phaseSeconds(s.phase)
        return Math.min(1, Math.max(0, 1 - remainingSeconds.value / total))
    })

    const phaseLabel = computed(() => PHASE_LABELS[state.value.phase])
    const nextPhaseLabel = computed(() => PHASE_LABELS[nextPhase()])
    const isWork = computed(() => state.value.phase === 'work')
    const isActive = computed(() => state.value.mode !== 'idle')

    // Numer pomodoro w cyklu: w pracy bieżące, w przerwie ostatnio ukończone.
    const cyclePosition = computed(() => {
        const s = state.value
        if (s.phase === 'work') return Math.min(s.longBreakEvery, s.completedInCycle + 1)
        return Math.max(1, s.completedInCycle)
    })

    // --- statystyki ---
    const todayKey = computed(() => toApiDate(new Date(now.value)))
    const todayCount = computed(() => history.value[todayKey.value]?.count ?? 0)
    const todayFocusMinutes = computed(() => Math.round((history.value[todayKey.value]?.focusSeconds ?? 0) / 60))

    // Ostatnie 7 dni (najstarszy → dziś) do słupków.
    const lastDays = computed(() => {
        const out: Array<{ day: string, label: string, count: number, focusMinutes: number, isToday: boolean }> = []
        const base = new Date(now.value)
        const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']   // skróty dni z tłumaczeń, kolejność jak getDay()
        for (let i = 6; i >= 0; i--) {
            const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() - i)
            const day = toApiDate(d)
            const entry = history.value[day]
            out.push({
                day,
                label: t(`tools.pomodoro.weekdays.${dayKeys[d.getDay()]}`),
                count: entry?.count ?? 0,
                focusMinutes: Math.round((entry?.focusSeconds ?? 0) / 60),
                isToday: i === 0,
            })
        }
        return out
    })

    const weekCount = computed(() => lastDays.value.reduce((sum, d) => sum + d.count, 0))
    const weekFocusMinutes = computed(() => lastDays.value.reduce((sum, d) => sum + d.focusMinutes, 0))

    // --- pełny ekran ---
    // Nakładka i tryb pełnoekranowy przeglądarki chodzą razem, ale nakładka
    // działa też wtedy, gdy przeglądarka odmówi requestFullscreen.
    const syncFullscreenFlag = () => {
        if (!document.fullscreenElement) fullscreen.value = false
    }

    const openFullscreen = async () => {
        if (!import.meta.client) return
        fullscreen.value = true
        if (!fullscreenListenerAttached) {
            fullscreenListenerAttached = true
            document.addEventListener('fullscreenchange', syncFullscreenFlag)
        }
        try {
            await document.documentElement.requestFullscreen?.()
        } catch { /* odmowa przeglądarki — zostaje sama nakładka */ }
    }

    const closeFullscreen = async () => {
        fullscreen.value = false
        if (!import.meta.client) return
        try {
            if (document.fullscreenElement) await document.exitFullscreen()
        } catch { /* i tak zamykamy nakładkę */ }
    }

    const toggleFullscreen = () => (fullscreen.value ? closeFullscreen() : openFullscreen())

    // --- globalny silnik (jeden na całą aplikację) ---
    // Jeden krok silnika: odświeża zegar i domyka fazę, której czas minął.
    const tick = () => {
        now.value = Date.now()
        if (state.value.mode === 'running' && now.value >= state.value.endTime) finish()
    }

    const ensureEngine = () => {
        if (!import.meta.client || engineStarted) return
        engineStarted = true
        loadHistory()
        load()
        intervalId = setInterval(tick, 250)
    }

    return {
        state, history, now, fullscreen,
        sounds: POMODORO_SOUNDS, presets: POMODORO_PRESETS,
        ensureEngine, tick, save,
        start, pause, resume, next, skip, reset, stopAlarm, testSound,
        openFullscreen, closeFullscreen, toggleFullscreen,
        setTaskName, setPhaseMinutes, setLongBreakEvery, applyPreset, toggleAutoStart, selectSound,
        restoreDefaults, clearHistory,
        phaseMinutes, phaseSeconds,
        remainingSeconds, overtimeSeconds, progress,
        phaseLabel, nextPhaseLabel, isWork, isActive, cyclePosition,
        todayCount, todayFocusMinutes, lastDays, weekCount, weekFocusMinutes,
    }
}
